import { Box, ButtonNew, Icon, SearchBar, Spacer, TextNew } from '@jfs/ui-new'
import { useFocusEffect, useNavigation } from '@react-navigation/core'
import { StackNavigationProp } from '@react-navigation/stack'
import { useCallback, useMemo, useState } from 'react'
import { Alert, FlatList, Image, ImageSourcePropType, Pressable, StyleSheet, View } from 'react-native'
import { AepsButton } from '../../comps/AepsButton'
import { AepsCheckBox } from '../../comps/AepsCheckBox'
import { Container } from '../../comps/Container'
import { IconWithText } from '../../comps/IconWithText'
import { ServiceHighlights } from '../../comps/ServiceHighlight'
import { VerticleDrawerDialog } from '../../comps/VerticleDrawerDialog'
import { RootStore, useLazyApiData, useStores } from '../../data/store'
import { BbpsStackParamList } from '../../nav/Bbps'
import { Screens } from '../../nav/Screens'

export interface FavoriteTransaction {
  billerMasterId: number
  order: {
    id: string
    status: string
    product: {
      id: number
      bbpsRegNo?: string
      name: string
      isBbps?: string
      attribute: {
        authenticatorList: string[]
        labelOfAuthenticators?: string[]
      }
      logoName?: string
    }
    payment: {
      paymentAt: string | number[] // Added number[] to match implementation
    }
    bill: {
      customerName: string
    }
    customerDetails: {
      jpbUserId: string
      nickName: string
    }
  }
  billerShortName?: string
  shortName?: string
  authenticators?: string[]
  creationDate?: number[]
}

export interface OrderHistoryItem {
  billerMasterId?: string | number
  order: {
    id: string
    status: string
    product: {
      id: number
      name: string
      isBbps?: string
      logoName?: string
      bbpsRegNo?: string
      attribute: {
        authenticatorList: string[]
        labelOfAuthenticators?: string[]
      }
    }
    payment: {
      paymentAt: string | number[] // Added number[] to match implementation
    }
    bill: {
      customerName: string
    }
    customerDetails: {
      jpbUserId: string
      nickName: string
    }
  }
}

export interface MenuPositionData {
  x: number
  y: number
  width: number
  height: number
}

export interface OpenMenuData {
  id: string
  isFavorite: boolean
  name: string
  orderId: string
  position: MenuPositionData
}

export interface CustomerInput {
  paramName: string
  paramValue: string
}

export interface BillerItem {
  product: {
    id: number
    displayName: string
    logoName?: string
  }
}

export interface CategoryDetailsParams {
  mobileNumber: string
  categoryId: string
  categoryName: string
  displayName: string
}

export const formatCreationDate = (dateInfo: number[] | string | undefined): string => {
  if (!dateInfo) return 'N/A'
  let date: Date
  if (Array.isArray(dateInfo)) {
    if (dateInfo.length < 3) return 'N/A'
    date = new Date(dateInfo[0], dateInfo[1] - 1, dateInfo[2])
  } else if (typeof dateInfo === 'string') {
    date = new Date(dateInfo)
  } else {
    return 'N/A'
  }
  if (isNaN(date.getTime())) return 'N/A'

  const options: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'long', year: 'numeric' }
  const formattedDate = date.toLocaleDateString('en-IN', options)
  return `Last transaction on ${formattedDate}`
}

export const getCategoryDetailsForFavorite = (item: FavoriteTransaction): CategoryDetailsParams => {
  const mobileNo = RootStore.BillPaymentAgentOnboard.tempCustomerData?.mobileNumber || '9999999999'
  const categoryId = '1000'
  const categoryName = 'Utility'
  return {
    mobileNumber: mobileNo,
    categoryId: categoryId,
    categoryName: categoryName,
    displayName: item.order.product.name,
  }
}

export const BbpsHomeScreen = () => {
  const navigation = useNavigation<StackNavigationProp<BbpsStackParamList>>()
  const {
    fetchFavoriteTransactionsBC,
    getOrderHistoryActionBC,
    manageFavTxnsActionBC,
    BillFetchActionBC,
    fetchUserActionBC,
    setAgentJFSUserId,
  } = useStores((root) => ({
    fetchFavoriteTransactionsBC: root.BillPaymentAgentOnboard.fetchFavoriteTransactionsBC,
    getOrderHistoryActionBC: root.BillPaymentAgentOnboard.getOrderHistoryActionBC,
    manageFavTxnsActionBC: root.BillPaymentAgentOnboard.manageFavTxnsActionBC,
    BillFetchActionBC: root.BillPaymentAgentOnboard.BillFetchActionBC,
    fetchUserActionBC: root.BillPaymentAgentOnboard.fetchUserActionBC,
    setAgentJFSUserId: root.BillPaymentAgentOnboard.setAgentJFSUserId,
  }))
  const [triggerManageFavTxns] = useLazyApiData(manageFavTxnsActionBC)
  const [loading, setLoading] = useState(true)
  const [favoriteTransactions, setFavoriteTransactions] = useState<FavoriteTransaction[]>([])
  const [orderHistory, setOrderHistory] = useState<OrderHistoryItem['order'][]>([])
  const [activeTab] = useState('payBills')
  const [showInactivityDialog, setShowInactivityDialog] = useState(false)
  const [checked, setChecked] = useState(false)
  const [customerJpbUserId] = useState<string | undefined>(undefined)
  const [openMenuId, setOpenMenuId] = useState<string | null>(null)
  const [openMenuData, setOpenMenuData] = useState<OpenMenuData | null>(null)

  const [searchText, setSearchText] = useState('')

  const [customerSearch, setCustomerSearch] = useState('')
  const [operatorSearch, setOperatorSearch] = useState('')

  const idToUse =
    customerJpbUserId || RootStore.BillPaymentAgentOnboard.tempCustomerData?.jfsUserId || '1972283249505670348'
  // const idToUse = '1972283249505670348'
  const jpbUserId = RootStore.BillPaymentAgentOnboard.tempCustomerData?.jfsUserId || ''

  const handleViewItemDetails = (item: FavoriteTransaction | OrderHistoryItem['order']) => {
    setOpenMenuId(null)

    // Normalize: Extract the 'order' object regardless of which list it came from
    const orderData = 'order' in item ? item.order : item
    const product = orderData?.product

    if (!product) {
      RootStore.message.setMessage('Product details missing', 'ERROR')
      return
    }

    // Map the data strictly to what BbpsBillerScreen expects
    const billerData = {
      product: {
        id: product.id,
        displayName: product.name,
        logoName: product.logoName || '',
        bbpsRegNo: product.bbpsRegNo || '',
        isBbps: product.isBbps || 'N', // Now TS recognizes this
      },
      authenticators: product.attribute?.authenticatorList || [],
      labels: product.attribute?.labelOfAuthenticators || [],
    }

    const categoryDetails = getCategoryDetailsForFavorite(
      'order' in item ? item : ({ order: item } as FavoriteTransaction),
    )

    navigation.navigate(Screens.BbpsBillerScreen as any, {
      billerDetails: billerData,
      productId: product.id, // <--- Add this line to satisfy the BbpsBillerParams type
      authenticators:
        product.attribute?.authenticatorList?.map((val, index) => ({
          authInfo: {
            displayName: product.attribute?.labelOfAuthenticators?.[index] || 'Detail',
            fieldValidationPattern: '.*',
            fieldErrorMessage: 'Invalid Input',
            dataType: 'AN',
            displayOrder: (index + 1).toString(),
          },
        })) || [],
      categoryDetails: categoryDetails,
      preFilledAuthenticators: product.attribute?.authenticatorList || [],
    })
  }

  const executeBillFetch = async (item: FavoriteTransaction | OrderHistoryItem['order']) => {
    setLoading(true)
    console.log('item billerProductId', item)
    // Extract data safely from either Favorite or Order History structure
    const orderData = 'order' in item ? item?.order : item
    const product = orderData?.product
    const billerProductId = product?.id
    const jfsUserId = orderData?.customerDetails?.jpbUserId || ''
    console.log('billerProductId product', product, billerProductId)
    console.log('Customer JFS User ID', jfsUserId)

    if (!product || !billerProductId || !jfsUserId) {
      console.log('Error', 'Incomplete transaction data. Cannot fetch bill.')
      console.log(
        'Error',
        'Favorite Item data is incomplete (missing product details). Cannot fetch bill.',
        billerProductId,
      )
      console.log('Favorite Item data is incomplete. Please try again later.', product)

      setLoading(false)
      return
    }

    if (!jfsUserId) {
      console.log('Error', 'Customer JFS User ID (jpbUserId) is missing. Cannot fetch bill.')

      setLoading(false)
      return
    }

    try {
      // 1. Prepare Authenticators (Customer Inputs)
      const authenticatorLabels = product.attribute?.labelOfAuthenticators || []
      const authenticatorValues = product.attribute?.authenticatorList || []

      if (authenticatorValues.length === 0) {
        console.log('Error', 'Biller authenticators are missing. Cannot fetch bill.')
        setLoading(false)
        return
      }

      const customerInputs: CustomerInput[] = authenticatorValues.map((value, index) => ({
        paramName: authenticatorLabels[index] || `Input ${index + 1}`,
        paramValue: value,
      }))

      // 2. Construct Payload for BillFetchActionBC
      const billFetchPayload = {
        domain: {
          id: 'PayMyBill',
          name: 'PayMyBill',
          transactionType: 'BUY',
          description: 'Bill fetch request',
        },
        initiatingEntity: {
          entityId: RootStore.agent.session.CHANNEL_ID || '3155',
        },
        product: {
          id: billerProductId,
          attribute: {
            authenticatorList: authenticatorValues,
          },
          displayName: product.name,
        },
      }

      // 3. Prepare Navigation Details
      const billerDetails: BillerItem = {
        product: {
          id: billerProductId,
          displayName: product.name,
          logoName: product.logoName || '',
        },
      }

      const categoryDetails = getCategoryDetailsForFavorite(
        'order' in item ? item : ({ order: item } as FavoriteTransaction),
      )
      console.log('see what billFetchPayload sending to Bill Fetch API', billFetchPayload)

      // 4. Call the Store Action (which triggers billFetchAPI)
      const response = await BillFetchActionBC(billFetchPayload, jfsUserId)

      console.log('see what response from Bill Fetch API', response)
      console.log('see what we are sending to Navigationnscreenm', {
        billDetails: response,
        billerDetails: billerDetails,
        categoryDetails: categoryDetails,
        customerInputs: customerInputs,
      })

      // 5. Navigate to Details Screen
      navigation.navigate(Screens.BbpsBillDetailsScreen as any, {
        billDetails: response,
        billerDetails: billerDetails,
        categoryDetails: categoryDetails,
        customerInputs: customerInputs,
      })
    } catch (error: any) {
      console.error('Bill Fetch Error:', error)
      console.log('Error fetching bill for favorite item:', error)

      console.log('Fetch Failed', error.message || 'Could not fetch bill details.')
    } finally {
      setLoading(false)
    }
  }

  const handleManageFavourite = async (orderId: string, setFavTxn: 'Y' | 'N', name: string) => {
    if (!idToUse || !orderId) {
      console.log('Error', 'Cannot process favorite: Missing Order ID or Customer ID.')
      RootStore.message.setMessage('Cannot process favorite: Missing ID.', 'ERROR')
      return
      return
    }
    try {
      setLoading(true)
      const requestBody = {
        orderId: orderId,
        setFavTxn: setFavTxn,
        remarks: `${setFavTxn === 'Y' ? 'Added' : 'Removed'} favorite for ${name}`,
        jpbUserId: idToUse,
      }
      const manageFavTranRes = await triggerManageFavTxns(requestBody)

      const successMsg = setFavTxn === 'Y' ? `${name} added to favourites` : `${name} removed from favourites`

      RootStore.message.handleSuccess({ message: successMsg })

      console.log('respoonse triggerManageFavTxns', manageFavTranRes)
      const action = setFavTxn === 'Y' ? 'added to' : 'removed from'
      await Promise.all([fetchFavorites(idToUse), fetchOrderHistory(idToUse)])
    } catch (error) {
      RootStore.message.handleError(error)
      console.error(`Error ${setFavTxn === 'Y' ? 'adding' : 'removing'} favorite:`, error)
    } finally {
      setLoading(false)
    }
  }

  const handleInactivityDialogPress = () => {
    setShowInactivityDialog(false)
  }

  const fetchOrderHistory = useCallback(
    async (jpbUserId: string) => {
      try {
        const response: any = await getOrderHistoryActionBC({ jpbUserId, config: { limit: '10', offset: '0' } })
        setOrderHistory(response?.map((item: any) => item.order) || [])
      } catch (error) {
        setOrderHistory([])
        return []
      }
    },
    [getOrderHistoryActionBC],
  )

  const fetchFavorites = useCallback(
    async (jpbUserId: string) => {
      try {
        const response: any = await fetchFavoriteTransactionsBC({ jpbUserId, config: { limit: '10', offset: '0' } })
        setFavoriteTransactions(response || [])
      } catch (error) {
        setFavoriteTransactions([])
      }
    },
    [fetchFavoriteTransactionsBC],
  )

  const initializeAgentAndFetchHistory = useCallback(async () => {
    setLoading(true)
    try {
      const agentMobile = RootStore.agent.agentInfo.agentMobileNumber

      if (!agentMobile) {
        console.error('Agent mobile number missing from store')
        return
      }

      // 1. Fetch the Agent's specific profile to get the 19-digit JFS ID
      const agentProfile = await fetchUserActionBC({ mobileNumber: agentMobile })
      const agentJfsId = agentProfile?.jfsUserId

      if (agentJfsId) {
        // 2. Store it for the Network Header to use
        setAgentJFSUserId(agentJfsId)

        // 3. Now fetch the lists using this ID
        await Promise.all([fetchFavorites(agentJfsId), fetchOrderHistory(agentJfsId)])
      } else {
        RootStore.message.setMessage('Could not resolve Agent ID', 'ERROR')
      }
    } catch (error) {
      console.error('Initialization Error:', error)
    } finally {
      setLoading(false)
    }
  }, [fetchUserActionBC, fetchFavorites, fetchOrderHistory, setAgentJFSUserId])

  const filteredFavorites = useMemo(() => {
    return favoriteTransactions.filter((item) => {
      // Accessing values from FavoriteTransaction.order
      const custName = item.order?.bill?.customerName?.toLowerCase() || ''
      const operatorName = item.order?.product?.name?.toLowerCase() || ''

      const matchesCustomer = custName.includes(customerSearch.toLowerCase())
      const matchesOperator = operatorName.includes(operatorSearch.toLowerCase())

      return matchesCustomer && matchesOperator
    })
  }, [customerSearch, operatorSearch, favoriteTransactions])

  // Filtered Order History
  const filteredOrderHistory = useMemo(() => {
    return orderHistory.filter((item) => {
      // Note: OrderHistoryItem['order'] is passed here directly
      const custName = item.bill?.customerName?.toLowerCase() || ''
      const operatorName = item.product?.name?.toLowerCase() || ''

      const matchesCustomer = custName.includes(customerSearch.toLowerCase())
      const matchesOperator = operatorName.includes(operatorSearch.toLowerCase())

      return matchesCustomer && matchesOperator
    })
  }, [customerSearch, operatorSearch, orderHistory])

  useFocusEffect(
    useCallback(() => {
      if (idToUse) {
        setLoading(true)
        Promise.all([fetchFavorites(idToUse), fetchOrderHistory(idToUse)]).finally(() => setLoading(false))
      }
      return () => {
        setCustomerSearch('')
        setOperatorSearch('')
        setOpenMenuId(null) // Good practice to close menus too
      }
    }, [idToUse, fetchOrderHistory, fetchFavorites]),
  )

  //   useFocusEffect(
  //   useCallback(() => {
  //     // Start the new initialization flow
  //     initializeAgentAndFetchHistory();

  //     return () => {
  //       setCustomerSearch('');
  //       setOperatorSearch('');
  //       setOpenMenuId(null);
  //     };
  //   }, [initializeAgentAndFetchHistory])
  // );

  const renderFavoriteItem = ({ item }: { item: FavoriteTransaction }) => {
    // const uniqueId = `fav-${item.billerMasterId}`
    const uniqueId = `fav-${item.order.id}`
    const isMenuOpen = openMenuId === uniqueId
    const billerName = item.order?.product?.name || 'Biller Name N/A'
    const customerName = item.order?.bill?.customerName || item.order?.customerDetails?.nickName || 'Customer Name N/A'
    const accountNumber = item.order?.product?.attribute?.authenticatorList?.[0] || 'N/A'
    const formattedDate = formatCreationDate(item.order?.payment?.paymentAt).replace(
      'Last transaction on ',
      'Last Link on ',
    )
    const PLACEHOLDER_ORDER_ID = item.order?.id.toString()

    const onViewDetails = () => {
      // setOpenMenuData(null)
      console.log('View Details', `Navigating to details for payment: ${billerName}`)
      console.log('Navigating to details for:', item.order.id)
      executeBillFetch(item)
    }

    const onToggleFavorite = () => {
      setOpenMenuData(null)
      handleManageFavourite(PLACEHOLDER_ORDER_ID, 'N', billerName)
    }

    return (
      <Box backgroundColor="WHITE" marginRight={16} width={300} zIndex={isMenuOpen ? 999 : 1}>
        <Box
          padding={15}
          marginTop={15}
          marginBottom={15}
          backgroundColor="BG_PRIMARY"
          borderRadius={15}
          position="relative">
          <Box flexDirection={'row'} justify={'space-between'} alignItems="flex-start">
            <Box flex={1}>
              <TextNew fontSize={15} color="BLACK" numberOfLines={1}>
                {billerName}
              </TextNew>
              <TextNew fontSize={14} color="DARK_GREY_2" fontWeight={'400'} numberOfLines={1}>
                {customerName}
              </TextNew>
              <TextNew fontSize={14} color="BLACK">
                {accountNumber}
              </TextNew>
            </Box>
            <Box marginTop={15} flexDirection={'row'} alignItems="center">
              <Spacer direction="horizontal" size={16} />
              <Icon id={'FAVOURITE_FILLED'} color="YELLOW" size={16} />
              <Spacer direction="horizontal" size={16} />
              <Box
                onPress={(e) => {
                  // Prevent the background Pressable from firing
                  e?.stopPropagation?.()
                  setOpenMenuId(isMenuOpen ? null : uniqueId)
                }}>
                <Icon id={'MENU'} color="BLACK" size={16} />
              </Box>
            </Box>
          </Box>

          {isMenuOpen && (
            <View style={styles.dropdownContainer}>
              {/* <Box
                onPress={(e: any) => {
                  e?.stopPropagation?.()
                  onViewDetails()
                  setOpenMenuId(null)
                }}
                padding={12}
                borderBottomWidth={1}
                borderColor="PRIMARY_GREY">
                <TextNew fontSize={14} color="BLACK">
                  View Item Details
                </TextNew>
              </Box> */}
              <Box
                onPress={(e: any) => {
                  e?.stopPropagation?.()
                  setOpenMenuId(null)
                  handleManageFavourite(PLACEHOLDER_ORDER_ID, 'N', billerName)
                }}
                padding={12}>
                <TextNew fontSize={14} color="ERROR">
                  ❌ Remove Favourite
                </TextNew>
              </Box>
            </View>
          )}

          <Box
            borderTopWidth={1}
            borderColor={'PRIMARY_GREY'}
            width={'100%'}
            marginTop={12}
            marginBottom={12}
            borderStyle={'dashed'}
          />
          <Box marginTop={15} flexDirection={'row'} justify={'space-between'}>
            <TextNew fontSize={14} color="DARK_GREY_2" fontWeight={'400'}>
              {formattedDate}
            </TextNew>
            <ButtonNew
              type={'secondarySmall'}
              title={'Pay'}
              color={'WHITE'}
              underlayColor={'BRAND_PRIMARY_WHITE'}
              borderColor={'PRIMARY'}
              titleColor={'PRIMARY'}
              // onPress={() => handlePayPress(item)}
              onPress={() => executeBillFetch(item)}
            />
          </Box>
        </Box>
      </Box>
    )
  }

  const renderOrderItem = ({ item }: { item: OrderHistoryItem['order'] }) => {
    const uniqueId = `order-${item.id}`
    const isMenuOpen = openMenuId === uniqueId
    const isCurrentlyFavorite = favoriteTransactions.some(
      (fav) => fav.order?.id === item.id || fav.billerMasterId.toString() === item.id,
    )
    const billerName = item.product?.name || 'Biller Name N/A'
    const customerName = item.bill?.customerName || 'Customer Name N/A'
    const accountNumber = item.product?.attribute?.authenticatorList?.[0] || 'N/A'
    const formattedDate = formatCreationDate(item.payment?.paymentAt).replace('Last transaction on ', 'Last Link on ')
    const statusColor = item.status === 'PAYMENT_RECEIVED' || item.status === 'CONFIRMED' ? 'AVAILABLE_GREEN' : 'ERROR'
    const statusBgColor = item.status === 'PAYMENT_RECEIVED' || item.status === 'CONFIRMED' ? 'DISMANTLED_GREEN' : 'RED'

    const onViewDetails = () => {
      setOpenMenuData(null)
      console.log('View Details', `Navigating to details for payment: ${billerName}`)
      console.log('Navigating to details for:', item.id)
      executeBillFetch(item)
    }

    const onToggleFavorite = () => {
      setOpenMenuData(null)
      handleManageFavourite(item.id, 'Y', billerName)
    }

    return (
      <Box backgroundColor="WHITE" marginRight={16} width={300} zIndex={isMenuOpen ? 9999 : 1}>
        <Box
          padding={15}
          marginTop={15}
          marginBottom={15}
          backgroundColor="BG_PRIMARY"
          borderRadius={15}
          position="relative">
          <Box flexDirection={'row'} justify={'space-between'} alignItems="flex-start">
            <Box flex={1}>
              <TextNew fontSize={15} color="BLACK" numberOfLines={1}>
                {billerName}
              </TextNew>
              <TextNew fontSize={14} color="DARK_GREY_2" fontWeight={'400'} numberOfLines={1}>
                {customerName}
              </TextNew>
              <TextNew fontSize={14} color="BLACK">
                {accountNumber}
              </TextNew>
            </Box>

            <Box flexDirection={'row'} alignItems="center" marginTop={15}>
              <Spacer direction="horizontal" size={8} />
              {/* <Icon id={'FAVOURITE_FILLED'} color="AVAILABLE_GREEN" size={16} /> */}
              <Box
                marginTop={15}
                onPress={(e: any) => {
                  e?.stopPropagation?.()
                  setOpenMenuId(isMenuOpen ? null : uniqueId)
                }}>
                <Icon id={'MENU'} color="BLACK" size={16} />
              </Box>
            </Box>
          </Box>

          {isMenuOpen && (
            <View style={styles.dropdownContainer}>
              {/* <Box
                onPress={(e: any) => {
                  e?.stopPropagation?.()
                  // onViewDetails();
                  handleViewItemDetails(item)
                  setOpenMenuId(null)
                }}
                padding={12}
                borderBottomWidth={1}
                borderColor="PRIMARY_GREY">
                <TextNew fontSize={14} color="BLACK">
                  View Item Details
                </TextNew>
              </Box> */}
              <Box
                onPress={(e: any) => {
                  e?.stopPropagation?.()
                  setOpenMenuId(null)
                  handleManageFavourite(item.id, isCurrentlyFavorite ? 'N' : 'Y', billerName)
                }}
                padding={12}>
                <TextNew fontSize={14} color={isCurrentlyFavorite ? 'ERROR' : 'PRIMARY'}>
                  {isCurrentlyFavorite ? '❌ Remove Favourite' : '⭐ Make Favourite'}
                </TextNew>
              </Box>
            </View>
          )}

          <Box
            borderTopWidth={1}
            borderColor={'PRIMARY_GREY'}
            width={'100%'}
            marginTop={12}
            marginBottom={12}
            borderStyle={'dashed'}
          />
          <Box marginTop={15} flexDirection={'row'} justify={'space-between'}>
            <TextNew fontSize={14} color="DARK_GREY_2">
              {formattedDate}
            </TextNew>
            <ButtonNew
              type={'secondarySmall'}
              title={'Pay'}
              color={'WHITE'}
              borderColor={'PRIMARY'}
              titleColor={'PRIMARY'}
              // onPress={() => handleReattemptPayPress(item)}
              onPress={() => executeBillFetch(item)}
            />
          </Box>
        </Box>
      </Box>
    )
  }

  const EmptyState = ({ message }: { message: string }) => (
    <Box
      width={300}
      height={150}
      justify="center"
      alignItems="center"
      backgroundColor="WHITE"
      borderRadius={15}
      borderWidth={1}
      borderColor="PRIMARY_GREY"
      borderStyle="dashed"
      marginRight={16}
      marginTop={15}>
      <Icon id="SEARCH" size={24} color="BRAND_COLOR_GRAY_1" />
      <Spacer size={8} />
      <TextNew fontSize={14} color="BRAND_COLOR_GRAY_1" textAlign="center">
        {message}
      </TextNew>
    </Box>
  )

  const handlePayBillsPress = async () => {
    navigation.navigate(Screens.CUSTOMER_ONBORD as never)
  }

  const handleHistoryPress = () => {
    // setShowInactivityDialog(true)
    navigation.navigate(Screens.TRANSACTION_DETAILS_SCREEN, {
      title: 'All Recent Payments',
      data: orderHistory,
      type: 'history',
    })
  }

  const searchList = (val: string) => {
    setSearchText(val)
  }

  const handleSeeAllFavorites = () => {
    // navigation.navigate(Screens.SeeAllFavourite)
    if (favoriteTransactions.length > 0) {
      navigation.navigate(Screens.TRANSACTION_DETAILS_SCREEN, {
        title: 'All Favorite Transactions',
        // data: favoriteTransactions,
        type: 'favorites',
      })
    } else {
      Alert.alert('No Favorites', 'You do not have any saved favorite transactions.')
    }
  }

  const handleSeeAllHistory = () => {
    // navigation.navigate(Screens.SeeAllOrderHistory)
    if (orderHistory.length > 0) {
      navigation.navigate(Screens.TRANSACTION_DETAILS_SCREEN, {
        title: 'All Recent Payments',
        // data: orderHistory,
        type: 'history',
      })
    } else {
      Alert.alert('No History', 'No recent payment history found.')
    }
  }

  const BBPSLogo: ImageSourcePropType = require('../../assets/images/BBPSLogo.png')

  return (
    <Container showFooter={false} overflow="auto">
      <Pressable
        style={{ flex: 1 }}
        onPress={() => setOpenMenuId(null)} // Closes menu when clicking background
        accessible={false}>
        <Box padding={16} backgroundColor="BG_PRIMARY" flexDirection={'column'} gap={10}>
          <Box flexDirection={'row'} justify={'space-between'}>
            <TextNew fontSize={25} fontWeight="bold">
              Bill
            </TextNew>
            <TextNew fontSize={14} color="BRAND_COLOR_GRAY_1">
              {RootStore.agent.agentInfo.agentUserName || 'Manish Kumar Tiwari'}
            </TextNew>
          </Box>
          <Box flexDirection="row" alignItems="center">
            <TextNew fontSize={14} color="BRAND_COLOR_GRAY_1">
              Powered by
            </TextNew>
            {/* <Spacer direction="horizontal" size={5} /> */}
            <Image source={BBPSLogo} style={{ width: 80, height: 20, resizeMode: 'contain' }} />
          </Box>
        </Box>

        <Box backgroundColor="WHITE" flexDirection={'row'} justify={'space-between'} marginBottom={16} marginTop={16}>
          <ButtonNew
            type={'secondarySmall'}
            title={'History'}
            color={'WHITE'}
            borderColor={'PRIMARY'}
            titleColor={'PRIMARY'}
            width={140}
            height={40}
            onPress={handleHistoryPress}
          />
          <ButtonNew
            type={'secondarySmall'}
            title={'Pay Bills'}
            color={'WHITE'}
            borderColor={'PRIMARY'}
            titleColor={'PRIMARY'}
            width={140}
            onPress={() => navigation.navigate(Screens.CUSTOMER_ONBORD as never)}
          />
        </Box>

        <Box flexDirection={'column'} gap={10}>
          <SearchBar
            onChange={(val) => setCustomerSearch(val)}
            placeholder="Search by customer name"
            value={customerSearch}
          />
          <SearchBar
            onChange={(val) => setOperatorSearch(val)}
            placeholder="Search by operator"
            value={operatorSearch}
          />
          {/* <SearchBar onChange={searchList} placeholder="Search by name or number" />
          <SearchBar onChange={searchList} placeholder="Search Operators" /> */}
        </Box>

        <Box marginTop={15} backgroundColor="WHITE" flexDirection={'row'} justify={'space-between'}>
          <TextNew fontSize={25} fontWeight="bold" color="BLACK">
            Favourites
          </TextNew>
          <Box onPress={handleSeeAllFavorites}>
            <TextNew fontSize={14} color="BRAND_COLOR_GRAY_1">
              See All
            </TextNew>
          </Box>
        </Box>

        <Box paddingLeft={16} paddingBottom={20}>
          <FlatList
            data={filteredFavorites}
            renderItem={renderFavoriteItem}
            horizontal
            onScrollBeginDrag={() => setOpenMenuId(null)} // Close menu on scroll
            keyExtractor={(item) => item.billerMasterId.toString()}
            showsHorizontalScrollIndicator={false}
            extraData={[customerSearch, operatorSearch, openMenuId]}
            removeClippedSubviews={false}
            maintainVisibleContentPosition={{ minIndexForVisible: 0 }}
            ListFooterComponent={<Spacer direction="horizontal" size={20} />}
            ListEmptyComponent={
              <EmptyState
                message={
                  customerSearch || operatorSearch ? 'No matching favorites found' : 'No favorite transactions yet'
                }
              />
            }
          />
        </Box>

        <Box marginTop={15} backgroundColor="WHITE" flexDirection={'row'} justify={'space-between'}>
          <TextNew fontSize={25} fontWeight="bold" color="BLACK">
            Recent Payments
          </TextNew>
          <Box onPress={handleSeeAllHistory}>
            <TextNew fontSize={14} color="BLACK">
              See All
            </TextNew>
          </Box>
        </Box>

        <Box paddingLeft={16} paddingBottom={20}>
          <FlatList
            data={filteredOrderHistory}
            renderItem={renderOrderItem}
            horizontal
            onScrollBeginDrag={() => setOpenMenuId(null)} // Close menu on scroll
            keyExtractor={(item) => item.id}
            showsHorizontalScrollIndicator={false}
            extraData={openMenuId}
            removeClippedSubviews={false}
            maintainVisibleContentPosition={{ minIndexForVisible: 0 }}
            ListFooterComponent={<Spacer direction="horizontal" size={20} />}
            ListEmptyComponent={
              <EmptyState
                message={customerSearch || operatorSearch ? 'No matching payments found' : 'No recent payment history'}
              />
            }
          />
        </Box>

        <Box backgroundColor="GRADIENT_ORANGE_FADE">
          <VerticleDrawerDialog
            show={showInactivityDialog}
            type="POSITIVE"
            onPress={handleInactivityDialogPress}
            message={''}>
            <TextNew fontSize={25} lineHeight={28} color="BLACK">
              {'Become a Bill Payment Agent with zero fees & grow your earnings'}
            </TextNew>
            <ServiceHighlights
              containerStyle={{
                marginTop: 30,
                marginBottom: 30,
                overflow: 'scroll',
                width: '100%',
              }}></ServiceHighlights>
            <Box marginBottom={40} marginTop={40} width={'100%'}>
              <IconWithText></IconWithText>
            </Box>
            <Box width={'100%'} marginBottom={40}>
              <AepsCheckBox
                text={'I agree to bill payment Terms & Conditions'}
                checked={checked}
                onChange={(isChecked) => setChecked(isChecked)}
                alignItems="center"
              />
            </Box>
            <Box width={'100%'}>
              <AepsButton onPress={handleInactivityDialogPress} title={'Register Now'} disabled={false} />
            </Box>
          </VerticleDrawerDialog>
        </Box>
      </Pressable>
    </Container>
  )
}

const styles = StyleSheet.create({
  dropdownContainer: {
    // CRITICAL: Absolute position to prevent black background & jumping
    position: 'absolute',
    top: 45,
    right: 15,
    zIndex: 9999,
    backgroundColor: 'white',
    borderRadius: 16,
    width: 220,
    padding: 8,
    // Shadow & Elevation
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 10,
    borderWidth: 1,
    borderColor: '#eee',
  },
})

// const onPrimaryButtonPress = async () => {
//     if (!checked) {
//       return
//     }
//     try {
//       const response = (await triggerAgentOnboarding({})) as unknown as AgentOnboardingResponse
//       const reasonCode = response?.responseObj?.agent?.agentStatus?.reasonCode
//       console.log('response from triggerAgentOnboarding API', response)
//       console.log('reasonCode from triggerAgentOnboarding API', reasonCode)

//       if (reasonCode === '3') {
//         navigation.navigate(Screens.CUSTOMER_ONBORD as never)
//         setShowInactivityDialog(false)
//       }
//     } catch (err) {
//       console.error('Agent Onboarding Failed:', err)
//     }
//   }

// useEffect(() => {
//   if (appType === '3') {
//     console.log('BBPS flow: Scheduling checkWhetherAgentExists API call in 30 seconds...')
//     const timerId = setTimeout(() => {
//       doesAgentExists({})
//     }, 30000)
//     return () => clearTimeout(timerId)
//   }
// }, [appType, doesAgentExists])

// <Box padding={15} marginTop={15} marginBottom={15} backgroundColor="#F8EBD7" borderRadius={15}>

//   <Box padding={16} backgroundColor="#F8EBD7" flexDirection={'column'} gap={10}></Box>
// useEffect(() => {
//   if (appType === '3') {
//     // console.log('BBPS flow: Triggering fetchFavoriteTransactionsAsync API call...')
//     const timerId = setTimeout(() => {
//       fetchFavoriteTransactionsAsync({})
//     }, 30000)
//     return () => clearTimeout(timerId)
//   }
// }, [appType])

// useEffect(() => {
//   if (appType === '3') {
//     console.log('BBPS flow: Triggering checkWhetherAgentExists API call...')
//     doesAgentExists({})
//   }
// }, [appType])

// <Box backgroundColor="WHITE">
//   <Box padding={15} marginTop={15} marginBottom={15} backgroundColor="BG_PRIMARY" borderRadius={15}>
//     <Box flexDirection={'row'} justify={'space-between'}>
//       <Box>
//         <TextNew fontSize={15} color="BLACK">
//           ICICI Bank FASTag
//         </TextNew>
//         <TextNew fontSize={14} color="DARK_GREY_2" fontWeight={'400'}>
//           Prakash
//         </TextNew>
//         <TextNew fontSize={14} color="BLACK">
//           MH034589
//         </TextNew>
//       </Box>
//       <Box marginTop={15} flexDirection={'row'}>
//         <Spacer direction="horizontal" size={16} />
//         <Icon id={'FAVOURITE_FILLED'} color="AVAILABLE_GREEN" size={16} />
//         <Icon id={'MENU'} color="AVAILABLE_GREEN" size={16} />
//       </Box>
//     </Box>
//     <Box
//       borderTopWidth={1}
//       borderColor={'PRIMARY_GREY'}
//       width={'100%'}
//       marginTop={12}
//       marginBottom={12}
//       borderStyle={'dashed'}
//     />
//     <Box marginTop={15} flexDirection={'row'} justify={'space-between'}>
//       <TextNew fontSize={14} color="DARK_GREY_2" fontWeight={'400'}>
//         Last Link on O5 june 2025
//       </TextNew>
//       <ButtonNew
//         type={'secondarySmall'}
//         title={'Pay'}
//         color={'WHITE'}
//         underlayColor={'BRAND_PRIMARY_WHITE'}
//         borderColor={'PRIMARY'}
//         titleColor={'PRIMARY'}
//         onPress={handlePayBillsPress}
//       />
//     </Box>
//   </Box>
// </Box>

// useEffect(() => {
//   if (appType === '3') {
//     console.log('BBPS flow: Scheduling checkWhetherAgentExists API call in 30 seconds...')
//     const timerId = setTimeout(() => {
//       doesAgentExists({})
//     }, 30000)
//     return () => clearTimeout(timerId)
//   }
// }, [appType, doesAgentExists])

// <Box padding={15} marginTop={15} marginBottom={15} backgroundColor="#F8EBD7" borderRadius={15}>

//   <Box padding={16} backgroundColor="#F8EBD7" flexDirection={'column'} gap={10}></Box>
// useEffect(() => {
//   if (appType === '3') {
//     // console.log('BBPS flow: Triggering fetchFavoriteTransactionsAsync API call...')
//     const timerId = setTimeout(() => {
//       fetchFavoriteTransactionsAsync({})
//     }, 30000)
//     return () => clearTimeout(timerId)
//   }
// }, [appType])

// useEffect(() => {
//   if (appType === '3') {
//     console.log('BBPS flow: Triggering checkWhetherAgentExists API call...')
//     doesAgentExists({})
//   }
// }, [appType])

// <Box backgroundColor="WHITE">
//               <Box padding={15} marginTop={15} marginBottom={15} backgroundColor="BG_PRIMARY" borderRadius={15}>
//                 <Box flexDirection={'row'} justify={'space-between'}>
//                   <Box>
//                     <TextNew fontSize={15} color="BLACK">
//                       ICICI Bank FASTag
//                     </TextNew>
//                     <TextNew fontSize={14} color="DARK_GREY_2" fontWeight={'400'}>
//                       Prakash
//                     </TextNew>
//                     <TextNew fontSize={14} color="BLACK">
//                       MH034589
//                     </TextNew>
//                   </Box>
//                   <Box marginTop={15} flexDirection={'row'}>
//                     <Spacer direction="horizontal" size={16} />
//                     <Icon id={'FAVOURITE_FILLED'} color="AVAILABLE_GREEN" size={16} />
//                     <Icon id={'MENU'} color="AVAILABLE_GREEN" size={16} />
//                   </Box>
//                 </Box>
//                 <Box
//                   borderTopWidth={1}
//                   borderColor={'PRIMARY_GREY'}
//                   width={'100%'}
//                   marginTop={12}
//                   marginBottom={12}
//                   borderStyle={'dashed'}
//                 />
//                 <Box marginTop={15} flexDirection={'row'} justify={'space-between'}>
//                   <TextNew fontSize={14} color="DARK_GREY_2" fontWeight={'400'}>
//                     Last Link on O5 june 2025
//                   </TextNew>
//                   <ButtonNew
//                     type={'secondarySmall'}
//                     title={'Pay'}
//                     color={'WHITE'}
//                     underlayColor={'BRAND_PRIMARY_WHITE'}
//                     borderColor={'PRIMARY'}
//                     titleColor={'PRIMARY'}
//                     onPress={handlePayBillsPress}
//                   />
//                 </Box>
//               </Box>
//             </Box>

// const renderOrderItem = ({ item }: { item: OrderHistoryItem['order'] }) => (
//     <Box
//       backgroundColor="WHITE"
//       // marginRight={2}
//       // width={300}
//       // borderRadius={2}
//       // borderWidth={1}
//       // borderColor="PRIMARY_GREY"
//       // overflow="hidden"
//     >
//       <Box padding={15} marginTop={15} marginBottom={15} backgroundColor="WHITE" flex={1}>
//         <Box flexDirection={'row'} justify={'space-between'} alignItems="flex-start">
//           <Box flexDirection={'row'} alignItems="center" flex={1}>
//             <Box marginRight={10}>
//               <Icon id={'HISTORY'} color="PRIMARY" size={24} />
//             </Box>
//             <TextNew fontSize={16} color="BLACK" fontWeight={'bold'} numberOfLines={1}>
//               {item.product.name}
//             </TextNew>
//             <TextNew fontSize={14} color="DARK_GREY_2" fontWeight={'400'}>
//               Prakash Chaudhary
//             </TextNew>
//             <TextNew fontSize={14} color="BLACK">
//               MH034589
//             </TextNew>
//           </Box>

//           <Box
//             marginTop={15}
//             flexDirection={'row'}
//             backgroundColor={item.status === 'PAYMENT_RECEIVED' ? 'DISMANTLED_GREEN' : 'RED'}
//             // paddingHorizontal={8}
//             // paddingVertical={4}
//             // borderRadius={12}
//             // marginLeft={10}
//           >
//             <Spacer direction="horizontal" size={16} />
//             <Icon id={'FAVOURITE_FILLED'} color="AVAILABLE_GREEN" size={16} />
//             <Icon id={'MENU'} color="AVAILABLE_GREEN" size={16} />
//             {/* <TextNew
//               fontSize={10}
//               color={item.status === 'PAYMENT_RECEIVED' ? 'AVAILABLE_GREEN' : 'ERROR'}
//               fontWeight={'bold'}>
//               {item.status.replace('_', ' ')}
//             </TextNew> */}
//           </Box>
//         </Box>

//         <Box marginBottom={15}>
//           <TextNew fontSize={14} color="DARK_GREY_2" fontWeight={'400'} numberOfLines={1}>
//             {item.bill.customerName || 'Customer'}
//           </TextNew>
//           <TextNew fontSize={14} color="BLACK" fontWeight={'600'} numberOfLines={1}>
//             Acc No: {item.product.attribute.authenticatorList?.[0] || 'N/A'}
//           </TextNew>
//         </Box>

//         <Box
//           borderTopWidth={1}
//           borderColor={'PRIMARY_GREY'}
//           width={'100%'}
//           marginTop={12}
//           marginBottom={12}
//           borderStyle={'dashed'}
//         />

//         <Box marginTop={15} flexDirection={'row'} justify={'space-between'} alignItems="center">
//           <TextNew fontSize={14} color="DARK_GREY_2" fontWeight={'400'}>
//             Last Linked on {formatCreationDate(item.payment.paymentAt).replace('Last transaction on ', '')}
//           </TextNew>

//           <ButtonNew
//             type={'secondarySmall'}
//             title={'Pay'}
//             color={'WHITE'}
//             underlayColor={'BRAND_PRIMARY_WHITE'}
//             borderColor={'PRIMARY'}
//             titleColor={'PRIMARY'}
//             // onPress={handlePayBillsPress}
//             onPress={() => Alert.alert('View Order', `Viewing order ${item.id}`)}
//           />
//         </Box>
//       </Box>
//     </Box>
//   )

// // ... (rest of the component logic)
//   const renderOrderItem = ({ item }: { item: OrderHistoryItem['order'] }) => (
//     <Box
//       backgroundColor="WHITE"
//       marginRight={16}
//       width={300}
//       borderRadius={10}
//       borderWidth={1}
//       borderColor="PRIMARY_GREY"
//       overflow="hidden"
//     >
//       <Box padding={15} backgroundColor="WHITE" flex={1}>
//         <Box flexDirection={'row'} justify={'space-between'} alignItems="flex-start" marginBottom={12}>
//           <Box flexDirection={'row'} alignItems="center" flex={1}>
//             <Box marginRight={10}>
//               <Icon id={'HISTORY'} color="PRIMARY" size={24} />
//             </Box>
//             <TextNew fontSize={16} color="BLACK" fontWeight={'bold'} numberOfLines={1}>
//               {item.product.name}
//             </TextNew>
//           </Box>

//           <Box
//             backgroundColor={item.status === 'PAYMENT_RECEIVED' ? 'DISMANTLED_GREEN' : 'RED'}
//             paddingHorizontal={8}
//             paddingVertical={4}
//             borderRadius={12}
//             marginLeft={10}
//           >
//             <TextNew
//               fontSize={10}
//               color={item.status === 'PAYMENT_RECEIVED' ? 'AVAILABLE_GREEN' : 'ERROR'}
//               fontWeight={'bold'}
//             >
//               {item.status.replace('_', ' ')}
//             </TextNew>
//           </Box>
//         </Box>

//         <Box marginBottom={15}>
//           <TextNew fontSize={14} color="DARK_GREY_2" fontWeight={'400'} numberOfLines={1}>
//             {item.bill.customerName || 'Customer'}
//           </TextNew>
//           <TextNew fontSize={14} color="BLACK" fontWeight={'600'} numberOfLines={1}>
//             Acc No: {item.product.attribute.authenticatorList?.[0] || 'N/A'}
//           </TextNew>
//         </Box>

//         <Box
//           borderTopWidth={1}
//           borderColor={'PRIMARY_GREY'}
//           width={'100%'}
//           marginBottom={12}
//         />

//         <Box flexDirection={'row'} justify={'space-between'} alignItems="center">
//           <TextNew fontSize={12} color="DARK_GREY_2" fontWeight={'400'}>
//             Paid on {formatCreationDate(item.payment.paymentAt).replace('Last transaction on ', '')}
//           </TextNew>

//           <ButtonNew
//             type={'secondarySmall'}
//             title={'View Details'}
//             onPress={() => Alert.alert('View Order', `Viewing order ${item.id}`)}
//           />
//         </Box>
//       </Box>
//     </Box>
//

//  useFocusEffect(
//     useCallback(() => {
//       const refreshData = async () => {
//         if (idToUse) {
//           setLoading(true)
//           console.log('FOCUS_EFFECT: Starting data refresh...')
//           await Promise.all([fetchFavorites(idToUse), fetchOrderHistory(idToUse)])
//           setLoading(false)
//         } else {
//           setLoading(false)
//         }
//       }
//       refreshData()

//       return () => {
//         console.log('FOCUS_EFFECT: BbpsHomeScreen lost focus.')
//       }
//     }, [idToUse, fetchOrderHistory, fetchFavorites]),
//   )

// const filteredFavorites = useMemo(() => {
//   if (!searchText) return favoriteTransactions
//   return favoriteTransactions.filter((item) => {
//     const name = item.order?.product?.name?.toLowerCase() || ''
//     const custName = item.order?.bill?.customerName?.toLowerCase() || '';
//   const operatorName = item.order?.product?.name?.toLowerCase() || '';

//   const matchesCustomer = custName.includes(customerSearch.toLowerCase());
//   const matchesOperator = operatorName.includes(operatorSearch.toLowerCase());
//     const mobile = item.order?.product?.attribute?.authenticatorList?.[0] || ''

//     return (
//       name.includes(searchText.toLowerCase()) ||
//       custName.includes(searchText.toLowerCase()) ||
//       mobile.includes(searchText)
//     )
//   })
// }, [searchText, favoriteTransactions])

// // Logic to filter Order History
// const filteredOrderHistory = useMemo(() => {
//   if (!searchText) return orderHistory
//   return orderHistory.filter((item) => {
//     const name = item.product?.name?.toLowerCase() || ''
//     const custName = item.bill?.customerName?.toLowerCase() || ''
//     const mobile = item.product?.attribute?.authenticatorList?.[0] || ''

//     return (
//       name.includes(searchText.toLowerCase()) ||
//       custName.includes(searchText.toLowerCase()) ||
//       mobile.includes(searchText)
//     )
//   })
// }, [searchText, orderHistory])

// Filtered Favorites (Corrected Path)
